import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Upload, Download, Trash2, Search, FolderOpen, AlertCircle } from 'lucide-react'
import { apiJson, type Page } from '../api'
import { canDeleteDocuments } from '../auth'
import Pager from '../components/Pager'
import SortableTh from '../components/SortableTh'

interface Doc {
  id: string
  title: string
  description?: string
  category: string
  fileName: string
  contentType: string
  fileSize: number | string
  uploadedBy?: string
  createdAt: string
}

const CATEGORIES = ['general', 'sop', 'policy', 'hr', 'finance', 'training']

const typeStyles: Record<string, { badge: string; label: string }> = {
  PDF: { badge: 'bg-red-50 text-red-600 ring-red-100', label: 'PDF' },
  DOCX: { badge: 'bg-blue-50 text-blue-600 ring-blue-100', label: 'DOC' },
  DOC: { badge: 'bg-blue-50 text-blue-600 ring-blue-100', label: 'DOC' },
  XLSX: { badge: 'bg-emerald-50 text-emerald-600 ring-emerald-100', label: 'XLS' },
  XLS: { badge: 'bg-emerald-50 text-emerald-600 ring-emerald-100', label: 'XLS' },
  PPTX: { badge: 'bg-orange-50 text-orange-600 ring-orange-100', label: 'PPT' },
  PNG: { badge: 'bg-violet-50 text-violet-600 ring-violet-100', label: 'IMG' },
  JPG: { badge: 'bg-violet-50 text-violet-600 ring-violet-100', label: 'IMG' },
  JPEG: { badge: 'bg-violet-50 text-violet-600 ring-violet-100', label: 'IMG' },
}

function extOf(fileName: string) {
  return (fileName.split('.').pop() ?? '').toUpperCase()
}

function typeStyle(ext: string) {
  return typeStyles[ext] ?? { badge: 'bg-slate-100 text-slate-500 ring-slate-200', label: ext.slice(0, 3) || 'FILE' }
}

function formatBytes(size: number | string) {
  const n = Number(size)
  if (!Number.isFinite(n) || n <= 0) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

export default function Documents() {
  const navigate = useNavigate()
  const [page_data, setPageData] = useState<Page<Doc> | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // list controls (server-side)
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState(searchParams.get('q') ?? '')
  const [q, setQ] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const [sort, setSort] = useState('createdAt')
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC')

  const allowDelete = canDeleteDocuments()

  // debounce the search box -> q (resets to page 1)
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(query.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [query])

  const handleSort = (field: string) => {
    if (sort === field) {
      setOrder(order === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSort(field)
      setOrder('ASC')
    }
    setPage(1)
  }

  const fetchDocuments = useCallback(async () => {
    setRefreshing(true)
    try {
      const params = new URLSearchParams({ page: String(page), size: String(size), sort, order })
      if (q) params.set('q', q)
      if (categoryFilter) params.set('category', categoryFilter)
      const data = await apiJson<Page<Doc>>(`/documents?${params}`)
      setPageData(data)
      setLoadError('')
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load documents')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [page, size, sort, order, q, categoryFilter])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments])

  const documents = useMemo(() => page_data?.data ?? [], [page_data])

  const download = async (doc: Doc) => {
    setActionError('')
    try {
      const data = await apiJson<{ url: string }>(`/documents/${doc.id}/download`)
      window.open(data.url, '_blank', 'noopener')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Download failed')
    }
  }

  const remove = async (doc: Doc) => {
    if (!window.confirm(`Delete “${doc.title}”? The file will be removed from storage.`)) return
    setDeletingId(doc.id)
    setActionError('')
    try {
      await apiJson(`/documents/${doc.id}`, { method: 'DELETE' })
      await fetchDocuments()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative">
            <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search documents…"
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 sm:w-64"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1) }}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm capitalize text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c} className="capitalize">{c}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => navigate('/documents/upload')}
          className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:shadow-xl hover:brightness-110"
        >
          <Upload size={16} />
          Upload Document
        </button>
      </div>

      {(loadError || actionError) && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" />
          {loadError || actionError}
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className={`overflow-x-auto transition-opacity ${refreshing ? 'opacity-60' : ''}`}>
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                <SortableTh label="Name" field="title" sort={sort} order={order} onSort={handleSort} />
                <SortableTh label="Category" field="category" sort={sort} order={order} onSort={handleSort} />
                <SortableTh label="Size" field="fileSize" sort={sort} order={order} onSort={handleSort} />
                <SortableTh label="Uploaded" field="createdAt" sort={sort} order={order} onSort={handleSort} />
                <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {documents.map((doc) => {
                const style = typeStyle(extOf(doc.fileName))
                return (
                  <tr key={doc.id} className="group transition-colors hover:bg-slate-50/70">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[10px] font-extrabold ring-1 ${style.badge}`}>
                          {style.label}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold">{doc.title}</p>
                          <p className="truncate text-xs text-slate-400">{doc.fileName}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium capitalize text-slate-600">
                        {doc.category}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-500">{formatBytes(doc.fileSize)}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-500">{(doc.createdAt ?? '').slice(0, 10)}</td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="inline-flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          onClick={() => download(doc)}
                          className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-600"
                          title="Download"
                        >
                          <Download size={15} />
                        </button>
                        {allowDelete && (
                          <button
                            onClick={() => remove(doc)}
                            disabled={deletingId === doc.id}
                            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                            title="Delete (admin / hr)"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {documents.length === 0 && !loadError && (
          <div className="flex flex-col items-center py-14 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <FolderOpen size={22} />
            </div>
            <p className="mt-3 text-sm font-semibold">No documents found</p>
            <p className="mt-1 text-xs text-slate-400">
              {query ? `Nothing matches “${query}”.` : 'Upload your first document to get started.'}
            </p>
          </div>
        )}

        <Pager
          page={page}
          totalPages={page_data?.totalPages ?? 1}
          total={page_data?.total ?? 0}
          size={size}
          onPage={setPage}
          onSize={(s) => { setSize(s); setPage(1) }}
        />
      </div>
    </div>
  )
}
